/**
 * Mobile PDP & Collection Product Controller
 * Handles variant selection, pricing sync, AJAX Add to Cart, Quick Add drawers, and search autocomplete drawers.
 */

if (typeof window.MobileProductManager === 'undefined') {
  window.MobileProductManager = class MobileProductManager {
  constructor(sectionContainer) {
    this.section = sectionContainer;
    
    // Form and input elements
    this.productForm = this.section.querySelector('.mobile-pdp__form');
    this.variantSelector = this.section.querySelector('.mobile-pdp__variant-select-hidden');
    this.sizeButtons = Array.from(this.section.querySelectorAll('.mobile-pdp__size-btn'));
    this.swatchButtons = Array.from(this.section.querySelectorAll('.mobile-pdp__swatch'));
    this.qtyInput = this.section.querySelector('.mobile-pdp__qty-input');
    
    // Display elements
    this.titleText = this.section.querySelector('.mobile-pdp__title');
    this.priceText = this.section.querySelector('.mobile-pdp__price');
    this.comparePriceText = this.section.querySelector('.mobile-pdp__price--compare');
    this.discountBadge = this.section.querySelector('.mobile-pdp__discount-badge');
    this.addToCartBtn = this.section.querySelector('.mobile-pdp__btn--primary');
    this.buyNowBtn = this.section.querySelector('.mobile-pdp__btn--secondary, [data-action="buy-now"]');

    // Sticky Buy Bar cached DOM references
    this.stickyBar = this.section.querySelector('.sticky-buy-bar') || document.getElementById('sticky-cart-bar');
    this.stickySelect = this.stickyBar ? this.stickyBar.querySelector('.sticky-buy-bar__select') : null;
    this.stickyAddBtn = this.stickyBar ? this.stickyBar.querySelector('.sticky-buy-bar__btn') : null;
    this.stickyPrice = this.stickyBar ? this.stickyBar.querySelector('.sticky-buy-bar__price') : null;
    
    // Option label elements cache
    this.optionLabels = new Map();
    this.section.querySelectorAll('.mobile-pdp__selected-value[data-option-label-index]').forEach(lbl => {
      const idx = parseInt(lbl.getAttribute('data-option-label-index'));
      if (!isNaN(idx)) this.optionLabels.set(idx, lbl);
    });

    // Variants JSON configuration
    this.variantData = this.getVariantData();
    
    this.init();
  }

  init() {
    this.bindOptionSelectors();
    this.bindQtySelectors();
    this.bindFormSubmit();
    this.bindBuyNow();
    this.initAccordions();
    this.initEditorialTabs();
    this.initSizeModal();
    this.initShareButton();
    this.initStickyBuy();
  }

  getVariantData() {
    const scriptTag = this.section.querySelector('[type="application/json"][id^="ProductJson-"]');
    if (!scriptTag) return [];
    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      return [];
    }
  }

  bindOptionSelectors() {
    // 1. Swatches Click (Colors, etc.)
    this.swatchButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const optionIndex = parseInt(btn.getAttribute('data-option-index'));
        const value = btn.getAttribute('data-value');
        
        // Update active class on elements of the same option group
        this.swatchButtons.filter(b => b.getAttribute('data-option-index') == optionIndex)
                          .forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        
        // Update textual label
        const label = this.optionLabels.get(optionIndex) || this.section.querySelector(`.mobile-pdp__selected-value[data-option-label-index="${optionIndex}"]`);
        if (label) label.textContent = value;
        
        this.onOptionChange();
      });
    });

    // 2. Size Button Options Click
    this.sizeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (btn.classList.contains('is-disabled')) return;

        const optionIndex = parseInt(btn.getAttribute('data-option-index'));
        const value = btn.getAttribute('data-value');
        
        this.sizeButtons.filter(b => b.getAttribute('data-option-index') == optionIndex)
                        .forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        
        const label = this.optionLabels.get(optionIndex) || this.section.querySelector(`.mobile-pdp__selected-value[data-option-label-index="${optionIndex}"]`);
        if (label) label.textContent = value;

        this.onOptionChange();
      });
    });
  }

  onOptionChange() {
    // Collect currently selected options values
    const selectedOptions = [];
    
    // Look at swatches
    const selectedSwatches = this.swatchButtons.filter(b => b.classList.contains('is-selected'));
    selectedSwatches.forEach(b => {
      const idx = parseInt(b.getAttribute('data-option-index'));
      selectedOptions[idx] = b.getAttribute('data-value');
    });

    // Look at sizes
    const selectedSizes = this.sizeButtons.filter(b => b.classList.contains('is-selected'));
    selectedSizes.forEach(b => {
      const idx = parseInt(b.getAttribute('data-option-index'));
      selectedOptions[idx] = b.getAttribute('data-value');
    });

    // Look at select dropdowns
    const selects = Array.from(this.section.querySelectorAll('select[data-option-index]'));
    selects.forEach(select => {
      const idx = parseInt(select.getAttribute('data-option-index'));
      if (!isNaN(idx)) selectedOptions[idx] = select.value;
    });

    // Match variant array
    const matchedVariant = this.variantData.find(variant => {
      return variant.options.every((opt, index) => {
        return opt === selectedOptions[index];
      });
    });

    this.updateVariantDisplay(matchedVariant);
  }

  updateVariantDisplay(variant) {
    if (!variant) {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = 'Unavailable';
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.textContent = 'Unavailable';
      }
      if (this.stickyAddBtn) {
        this.stickyAddBtn.disabled = true;
        this.stickyAddBtn.textContent = 'Unavailable';
      }
      return;
    }

    // 1. Update hidden select input value
    if (this.variantSelector) {
      this.variantSelector.value = variant.id;
    }

    // 2. Sync size buttons, swatches, and labels to match variant options
    if (variant.options && Array.isArray(variant.options)) {
      variant.options.forEach((optValue, optIdx) => {
        // Update size buttons
        this.sizeButtons.forEach(btn => {
          if (parseInt(btn.getAttribute('data-option-index')) === optIdx) {
            if (btn.getAttribute('data-value') === optValue) {
              btn.classList.add('is-selected');
            } else {
              btn.classList.remove('is-selected');
            }
          }
        });

        // Update color swatches
        this.swatchButtons.forEach(btn => {
          if (parseInt(btn.getAttribute('data-option-index')) === optIdx) {
            if (btn.getAttribute('data-value') === optValue) {
              btn.classList.add('is-selected');
            } else {
              btn.classList.remove('is-selected');
            }
          }
        });

        // Update selected option text label
        const label = this.optionLabels.get(optIdx) || this.section.querySelector(`.mobile-pdp__selected-value[data-option-label-index="${optIdx}"]`);
        if (label) label.textContent = optValue;

        // Update option select elements if any
        const select = this.section.querySelector(`select[data-option-index="${optIdx}"]`);
        if (select) select.value = optValue;
      });
    }

    // 3. Update pricing display
    if (this.priceText) {
      this.priceText.textContent = this.formatMoney(variant.price);
    }
    
    if (this.comparePriceText) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceText.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePriceText.style.display = 'inline';
        
        if (this.discountBadge) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          this.discountBadge.textContent = `${pct}% OFF`;
          this.discountBadge.style.display = 'inline-block';
        }
      } else {
        this.comparePriceText.style.display = 'none';
        if (this.discountBadge) this.discountBadge.style.display = 'none';
      }
    }

    // 4. Update Buy Button & Sticky Buy Bar states
    if (this.addToCartBtn) {
      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = 'Add to Cart';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = 'Sold Out';
      }
    }

    if (this.buyNowBtn) {
      if (variant.available) {
        this.buyNowBtn.disabled = false;
        this.buyNowBtn.textContent = 'Buy It Now';
      } else {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.textContent = 'Sold Out';
      }
    }

    if (this.stickyBar) {
      if (this.stickySelect) this.stickySelect.value = variant.id;
      if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(variant.price);
      if (this.stickyAddBtn) {
        this.stickyAddBtn.disabled = !variant.available;
        this.stickyAddBtn.textContent = variant.available ? 'Add' : 'Sold Out';
      }
    }

    // 5. Update gallery slide image to match variant's featured media (if assigned)
    if (variant.featured_media && window.Shopify) {
      const mediaId = variant.featured_media.id;
      const gallery = this.section.querySelector('.product-gallery');
      if (gallery && gallery._galleryInstance) {
        const slideIndex = gallery._galleryInstance.slides.findIndex(slide => {
          return slide.getAttribute('data-media-id') == mediaId;
        });
        if (slideIndex >= 0) {
          gallery._galleryInstance.scrollToIndex(slideIndex);
        }
      }
    }

    // 6. Update browser URL parameter
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {}

    // Trigger update on custom event listeners
    document.dispatchEvent(new CustomEvent('product:variant-change', { detail: variant }));
  }

  bindQtySelectors() {
    const qtyWrap = this.section.querySelector('.mobile-pdp__qty-wrapper');
    if (!qtyWrap || qtyWrap._qtyBound) return;
    qtyWrap._qtyBound = true;

    qtyWrap.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-action]');
      if (!btn) return;

      e.preventDefault();
      const action = btn.getAttribute('data-qty-action');
      const currentVal = parseInt(this.qtyInput.value || 1);

      if (action === 'minus') {
        const newVal = Math.max(1, currentVal - 1);
        if (this.qtyInput) this.qtyInput.value = newVal;
      } else if (action === 'plus') {
        const newVal = currentVal + 1;
        if (this.qtyInput) this.qtyInput.value = newVal;
      }
    });
  }

  bindFormSubmit() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      
      if (!this.addToCartBtn || this.addToCartBtn.disabled) return;
      
      const stickyAddBtn = this.stickyAddBtn || this.section.querySelector('.sticky-buy-bar__btn');
      
      this.addToCartBtn.disabled = true;
      const originalText = this.addToCartBtn.textContent;
      this.addToCartBtn.innerHTML = `<span class="loading-spinner">Adding...</span>`;

      if (stickyAddBtn) {
        stickyAddBtn.disabled = true;
        stickyAddBtn.textContent = 'Adding...';
      }

      const formData = new FormData(this.productForm);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      })
      .then(res => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(item => {
        // Dispatch document event for cart sync listeners
        document.dispatchEvent(new CustomEvent('cart:after-add', { detail: item }));
        
        // Animate Success State
        this.addToCartBtn.textContent = 'Added Successfully ✓';
        this.addToCartBtn.style.backgroundColor = '#28a745';
        
        if (stickyAddBtn) {
          stickyAddBtn.textContent = 'Added ✓';
          stickyAddBtn.style.backgroundColor = '#28a745';
        }

        setTimeout(() => {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.textContent = originalText;
          this.addToCartBtn.style.backgroundColor = '';
          if (stickyAddBtn) {
            stickyAddBtn.disabled = false;
            stickyAddBtn.textContent = 'Add';
            stickyAddBtn.style.backgroundColor = '';
          }
        }, 2000);
      })
      .catch(err => {
        console.error('PDP Form add to cart error:', err);
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = 'Error, try again';
        if (stickyAddBtn) {
          stickyAddBtn.disabled = false;
          stickyAddBtn.textContent = 'Add';
        }
        setTimeout(() => {
          this.addToCartBtn.textContent = originalText;
        }, 2000);
      });
    });
  }

  bindBuyNow() {
    if (!this.buyNowBtn || !this.productForm) return;

    this.buyNowBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.buyNowBtn.disabled) return;

      const originalText = this.buyNowBtn.textContent;
      this.buyNowBtn.disabled = true;
      if (this.addToCartBtn) this.addToCartBtn.disabled = true;
      this.buyNowBtn.innerHTML = '<span class="loading-spinner">Adding...</span>';

      const formData = new FormData(this.productForm);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' }
      })
      .then(res => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(item => {
        document.dispatchEvent(new CustomEvent('cart:after-add', { detail: item }));
        window.location.href = '/cart';
      })
      .catch(err => {
        console.error('Buy Now add to cart error, falling back to standard submit:', err);
        let ret = this.productForm.querySelector('input[name="return_to"]');
        if (!ret) {
          ret = document.createElement('input');
          ret.type = 'hidden';
          ret.name = 'return_to';
          ret.value = '/cart';
          this.productForm.appendChild(ret);
        }
        this.productForm.submit();
      });
    });
  }

  initAccordions() {
    const accordions = Array.from(this.section.querySelectorAll('.mobile-pdp__tab'));
    accordions.forEach(tab => {
      if (tab._tabBound) return;
      tab._tabBound = true;

      const trigger = tab.querySelector('.mobile-pdp__tab-trigger');
      const content = tab.querySelector('.mobile-pdp__tab-content');
      if (!trigger || !content) return;
      
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isActive = tab.classList.contains('is-active');
        
        accordions.forEach(t => {
          t.classList.remove('is-active');
          const c = t.querySelector('.mobile-pdp__tab-content');
          const trig = t.querySelector('.mobile-pdp__tab-trigger');
          if (c) c.style.maxHeight = '0';
          if (trig) trig.setAttribute('aria-expanded', 'false');
        });

        if (!isActive) {
          tab.classList.add('is-active');
          trigger.setAttribute('aria-expanded', 'true');
          const innerRte = content.querySelector('.mobile-pdp__tab-inner');
          if (innerRte) {
            const isDesc = (trigger.textContent || '').toLowerCase().includes('description');
            if (isDesc) {
              this.cleanDescriptionTabContent(innerRte);
            } else {
              this.cleanProductDescription(innerRte);
            }
          }
          const height = innerRte ? Math.max(innerRte.scrollHeight, content.scrollHeight) : content.scrollHeight;
          content.style.maxHeight = `${height + 50}px`;
        }
      });
    });

    // Run clean pass on load for all accordion RTE tabs
    this.section.querySelectorAll('.mobile-pdp__tab-inner').forEach(inner => {
      const tab = inner.closest('.mobile-pdp__tab');
      const trig = tab ? tab.querySelector('.mobile-pdp__tab-trigger') : null;
      const isDesc = trig && (trig.textContent || '').toLowerCase().includes('description');
      if (isDesc) {
        this.cleanDescriptionTabContent(inner);
      } else {
        this.cleanProductDescription(inner);
      }
    });
  }

  /* Horizontal Editorial Tabs switching */
  initEditorialTabs() {
    const tabGroups = Array.from(this.section.querySelectorAll('.mobile-pdp__horizontal-tabs'));
    tabGroups.forEach(group => {
      const btns = Array.from(group.querySelectorAll('.mobile-pdp__htab-btn'));
      const panels = Array.from(group.querySelectorAll('.mobile-pdp__htab-panel'));

      btns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetTab = btn.getAttribute('data-htab');

          btns.forEach(b => b.classList.remove('is-active'));
          panels.forEach(p => p.classList.remove('is-active'));

          btn.classList.add('is-active');
          const matchingPanel = group.querySelector(`.mobile-pdp__htab-panel[data-htab-panel="${targetTab}"]`);
          if (matchingPanel) {
            matchingPanel.classList.add('is-active');
          }
        });
      });
    });
  }

  /* Size & Measurement Guide Modal Drawer */
  initSizeModal() {
    const backdrop = document.querySelector('[data-size-modal-backdrop]');
    const modal = document.querySelector('[data-size-modal]');
    const openBtns = Array.from(this.section.querySelectorAll('[data-open-size-modal]'));
    const closeBtns = Array.from(document.querySelectorAll('[data-close-size-modal], [data-size-modal-backdrop]'));

    if (!modal) return;

    openBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (backdrop) backdrop.classList.add('is-active');
        modal.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      });
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (backdrop) backdrop.classList.remove('is-active');
        modal.classList.remove('is-active');
        document.body.style.overflow = '';
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-active')) {
        if (backdrop) backdrop.classList.remove('is-active');
        modal.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    });
  }

  /* Share Product Outfit Button */
  initShareButton() {
    const shareBtns = Array.from(this.section.querySelectorAll('[data-share-pdp]'));
    shareBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const shareData = {
          title: document.title,
          url: window.location.href
        };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
          } catch (err) {}
        } else {
          try {
            await navigator.clipboard.writeText(window.location.href);
            const originalText = btn.querySelector('span') ? btn.querySelector('span').textContent : 'Share';
            if (btn.querySelector('span')) btn.querySelector('span').textContent = 'Link Copied ✓';
            setTimeout(() => {
              if (btn.querySelector('span')) btn.querySelector('span').textContent = originalText;
            }, 2000);
          } catch (err) {}
        }
      });
    });
  }

  cleanDescriptionTabContent(container) {
    if (!container || container._cleanedDesc) return;
    container._cleanedDesc = true;

    this.cleanProductDescription(container);

    // Look for explicit heading elements like H1-H6 or P > STRONG matching structured section keywords
    const targetKeywords = ['highlights', 'specifications', 'fit & sizing', 'fit & size', 'fit and sizing', 'fabric & care'];
    const headings = Array.from(container.querySelectorAll('h1, h2, h3, h4, h5, h6, strong, b'));

    for (const heading of headings) {
      const text = (heading.textContent || '').trim().toLowerCase();
      const isTargetHeader = targetKeywords.some(kw => text === kw || text.startsWith(kw));

      if (isTargetHeader) {
        let parentBlock = heading.closest('p, div, h1, h2, h3, h4, h5, h6') || heading;
        if (parentBlock && parentBlock !== container) {
          let next = parentBlock;
          while (next) {
            const toRemove = next;
            next = next.nextElementSibling;
            // Prevent removing if it's the only block left
            if (toRemove !== container && container.children.length > 1) {
              toRemove.remove();
            }
          }
          break;
        }
      }
    }
  }

  cleanProductDescription(container) {
    if (!container || container._cleaned) return;
    container._cleaned = true;

    // Remove only explicit duplicate price/taxes or rating inputs, preserve all product text
    const remaining = Array.from(container.querySelectorAll('*'));
    remaining.forEach(el => {
      const txt = (el.textContent || '').trim().toLowerCase();
      if (
        txt.includes('inclusive of all taxes') ||
        txt.includes('regular price') ||
        txt.includes('0 (0 ratings)') ||
        txt.startsWith('0 0') ||
        el.querySelector('input[type="radio"]') !== null
      ) {
        el.remove();
      }
    });
  }

  initStickyBuy() {
    const stickyBar = this.stickyBar;
    if (!stickyBar) return;

    if (this.stickySelect) {
      this.stickySelect.addEventListener('change', (e) => {
        const val = e.target.value;
        const variant = this.variantData.find(v => v.id == val);
        if (variant) {
          this.updateVariantDisplay(variant);
        }
      });
    }

    if (this.stickyAddBtn) {
      this.stickyAddBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.productForm) {
          if (typeof this.productForm.requestSubmit === 'function') {
            this.productForm.requestSubmit();
          } else {
            this.productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        }
      });
    }

    const targetEl = this.addToCartBtn || this.titleText;
    if (targetEl) {
      let isTicking = false;
      window.addEventListener('scroll', () => {
        if (!isTicking) {
          window.requestAnimationFrame(() => {
            const targetRect = targetEl.getBoundingClientRect();
            if (targetRect.bottom < 0) {
              stickyBar.classList.add('is-visible');
            } else {
              stickyBar.classList.remove('is-visible');
            }
            isTicking = false;
          });
          isTicking = true;
        }
      }, { passive: true });
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = (window.theme && window.theme.moneyFormat) ? window.theme.moneyFormat : 'Rs. {{amount}}';
    
    function defaultOption(opt, def) {
      return (typeof opt == 'undefined' ? def : opt);
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal   = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return 0;

      number = (number/100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';

      return dollarsAmount + centsAmount;
    }

    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }
};
}

// Quick Add Drawer controller for Collection grids
if (typeof window.QuickAddManager === 'undefined') {
  window.QuickAddManager = class QuickAddManager {
  constructor() {
    this.elements = null;
    this.init();
  }

  getElements() {
    if (!this.elements) {
      this.elements = {
        drawer: document.getElementById('quick-add-drawer'),
        backdrop: document.getElementById('quick-add-backdrop'),
        closeBtn: document.getElementById('quick-add-close'),
        formContent: document.getElementById('quick-add-form-content')
      };
    }
    return this.elements;
  }

  init() {
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('#quick-add-close, .quick-add-drawer__close');
      if (closeBtn) {
        e.preventDefault();
        this.close();
        return;
      }

      const backdrop = e.target.closest('#quick-add-backdrop, .quick-add-backdrop');
      if (backdrop) {
        e.preventDefault();
        this.close();
        return;
      }

      const trigger = e.target.closest('[data-quick-add-trigger]');
      if (trigger) {
        e.preventDefault();
        const handle = trigger.getAttribute('data-quick-add-trigger');
        if (handle) this.open(handle);
      }
    });
  }

  open(handle) {
    const { drawer, backdrop, formContent } = this.getElements();
    if (!drawer || !formContent) return;

    formContent.innerHTML = `<div style="text-align:center;padding:30px;"><div class="loading-spinner">Loading options...</div></div>`;
    drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');

    fetch(`/products/${handle}?view=quick-add-ajax`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch options');
        return res.text();
      })
      .then(html => {
        formContent.innerHTML = html;
        this.bindQuickAddEvents(formContent);
      })
      .catch(err => {
        console.error('Quick add fetch error:', err);
        formContent.innerHTML = `<div style="padding:20px;text-align:center;">Failed to load options. Please try again.</div>`;
      });
  }

  close() {
    const { drawer, backdrop } = this.getElements();
    if (drawer) drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
  }

  bindQuickAddEvents(formContent) {
    const form = formContent.querySelector('form');
    if (!form) return;

    const sizeButtons = Array.from(formContent.querySelectorAll('.mobile-pdp__size-btn'));
    const swatches = Array.from(formContent.querySelectorAll('.mobile-pdp__swatch'));
    const hiddenSelect = form.querySelector('[name="id"]');
    const jsonTag = formContent.querySelector('[type="application/json"]');
    
    if (!jsonTag) return;
    let variants = [];
    try { variants = JSON.parse(jsonTag.textContent); } catch (e) { variants = []; }

    const updateQuickVariant = () => {
      const selected = [];
      swatches.filter(s => s.classList.contains('is-selected')).forEach(s => {
        selected[parseInt(s.getAttribute('data-option-index'))] = s.getAttribute('data-value');
      });
      sizeButtons.filter(s => s.classList.contains('is-selected')).forEach(s => {
        selected[parseInt(s.getAttribute('data-option-index'))] = s.getAttribute('data-value');
      });

      const match = variants.find(v => {
        return v.options.every((o, idx) => o === selected[idx]);
      });

      const addBtn = form.querySelector('[type="submit"]');
      if (match) {
        if (hiddenSelect) hiddenSelect.value = match.id;
        if (addBtn) {
          addBtn.disabled = !match.available;
          addBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
        }
      } else {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
        }
      }
    };

    swatches.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const optIdx = parseInt(btn.getAttribute('data-option-index'));
        swatches.filter(s => s.getAttribute('data-option-index') == optIdx).forEach(s => s.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        updateQuickVariant();
      });
    });

    sizeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const optIdx = parseInt(btn.getAttribute('data-option-index'));
        sizeButtons.filter(s => s.getAttribute('data-option-index') == optIdx).forEach(s => s.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        updateQuickVariant();
      });
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        if (submitBtn.disabled) return;
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding...';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'Accept': 'application/json' }
      })
      .then(res => {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(item => {
        if (submitBtn) {
          submitBtn.textContent = 'Added ✓';
          submitBtn.style.backgroundColor = '#28a745';
        }
        document.dispatchEvent(new CustomEvent('cart:after-add', { detail: item }));
        setTimeout(() => {
          this.close();
        }, 800);
      })
      .catch(err => {
        console.error('Quick Add submit error:', err);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Error, try again';
        }
      });
    });
  }
};
}

// Global Wishlist Controller
if (typeof window.WishlistManager === 'undefined') {
  window.WishlistManager = class WishlistManager {
  constructor() {
    this.init();
  }

  getSavedList() {
    try {
      return JSON.parse(localStorage.getItem('theme-wishlist') || '[]');
    } catch (e) {
      return [];
    }
  }

  saveList(list) {
    try {
      localStorage.setItem('theme-wishlist', JSON.stringify(list));
    } catch (e) {}
  }

  syncStates() {
    const list = this.getSavedList();
    const set = new Set(list);
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const pId = btn.getAttribute('data-wishlist-toggle');
      if (pId && set.has(pId)) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    });
  }

  init() {
    this.syncStates();

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const pId = btn.getAttribute('data-wishlist-toggle');
      if (!pId) return;

      let list = this.getSavedList();
      const isActive = btn.classList.contains('is-active');

      if (isActive) {
        btn.classList.remove('is-active');
        list = list.filter(id => id !== pId);
      } else {
        btn.classList.add('is-active');
        if (!list.includes(pId)) list.push(pId);
      }

      this.saveList(list);

      // Sync all instances of the same product ID on the page
      document.querySelectorAll(`[data-wishlist-toggle="${pId}"]`).forEach(b => {
        if (list.includes(pId)) b.classList.add('is-active');
        else b.classList.remove('is-active');
      });

      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { list, productId: pId, isWishlisted: !isActive } }));
    });
  }
};
}

// AJAX Card Form Handler for direct single variant + Add buttons
if (typeof window.AjaxCartFormHandler === 'undefined') {
  window.AjaxCartFormHandler = class AjaxCartFormHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-ajax-card-form]');
      if (!form) return;

      e.preventDefault();
      const btn = form.querySelector('.mobile-product-card__quick-add') || form.querySelector('button[type="submit"]');
      if (btn) {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.dataset.originalContent = btn.innerHTML;
        btn.innerHTML = `<span>Adding...</span>`;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'Accept': 'application/json' }
      })
      .then(res => {
        if (!res.ok) throw new Error('Failed to add product');
        return res.json();
      })
      .then(item => {
        if (btn) {
          btn.innerHTML = `<span>Added ✓</span>`;
          btn.style.backgroundColor = '#28a745';
          btn.style.color = '#ffffff';
          btn.style.borderColor = '#28a745';
          setTimeout(() => {
            btn.disabled = false;
            if (btn.dataset.originalContent) btn.innerHTML = btn.dataset.originalContent;
            btn.style.backgroundColor = '';
            btn.style.color = '';
            btn.style.borderColor = '';
          }, 2000);
        }
        document.dispatchEvent(new CustomEvent('cart:after-add', { detail: item }));
      })
      .catch(err => {
        console.error('AJAX Card Add error:', err);
        if (btn) {
          btn.innerHTML = `<span>Error</span>`;
          setTimeout(() => {
            btn.disabled = false;
            if (btn.dataset.originalContent) btn.innerHTML = btn.dataset.originalContent;
          }, 2000);
        }
      });
    });
  }
};
}

// Grid Sort & Filter Manager
if (typeof window.GridSortFilterManager === 'undefined') {
  window.GridSortFilterManager = class GridSortFilterManager {
  constructor() {
    this.activeSort = 'featured';
    this.filters = {
      inStock: false,
      minPrice: null,
      maxPrice: null
    };
    this.elements = null;
    this.init();
  }

  getElements() {
    if (!this.elements) {
      this.elements = {
        openBtns: Array.from(document.querySelectorAll('[data-open-filter-drawer]')),
        closeBtns: Array.from(document.querySelectorAll('[data-close-filter-drawer]')),
        backdrop: document.querySelector('[data-filter-backdrop]'),
        drawer: document.querySelector('[data-filter-drawer]'),
        sortSelects: Array.from(document.querySelectorAll('[data-sort-select], [data-drawer-sort-select]')),
        availabilityCheckboxes: Array.from(document.querySelectorAll('[data-filter-availability="in-stock"]')),
        minPriceInputs: Array.from(document.querySelectorAll('[data-filter-min-price]')),
        maxPriceInputs: Array.from(document.querySelectorAll('[data-filter-max-price]')),
        resetBtns: Array.from(document.querySelectorAll('[data-reset-filters]')),
        applyBtns: Array.from(document.querySelectorAll('[data-apply-filters]')),
        badgeCounts: Array.from(document.querySelectorAll('[data-filter-count]'))
      };
    }
    return this.elements;
  }

  refreshElements() {
    this.elements = {
      openBtns: Array.from(document.querySelectorAll('[data-open-filter-drawer]')),
      closeBtns: Array.from(document.querySelectorAll('[data-close-filter-drawer]')),
      backdrop: document.querySelector('[data-filter-backdrop]'),
      drawer: document.querySelector('[data-filter-drawer]'),
      sortSelects: Array.from(document.querySelectorAll('[data-sort-select], [data-drawer-sort-select]')),
      availabilityCheckboxes: Array.from(document.querySelectorAll('[data-filter-availability="in-stock"]')),
      minPriceInputs: Array.from(document.querySelectorAll('[data-filter-min-price]')),
      maxPriceInputs: Array.from(document.querySelectorAll('[data-filter-max-price]')),
      resetBtns: Array.from(document.querySelectorAll('[data-reset-filters]')),
      applyBtns: Array.from(document.querySelectorAll('[data-apply-filters]')),
      badgeCounts: Array.from(document.querySelectorAll('[data-filter-count]'))
    };
    return this.elements;
  }

  init() {
    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-open-filter-drawer]');
      if (openBtn) {
        e.preventDefault();
        this.openDrawer();
        return;
      }

      const closeBtn = e.target.closest('[data-close-filter-drawer], [data-filter-backdrop]');
      if (closeBtn) {
        e.preventDefault();
        this.closeDrawer();
        return;
      }

      const resetBtn = e.target.closest('[data-reset-filters]');
      if (resetBtn) {
        e.preventDefault();
        this.resetFilters();
        return;
      }

      const applyBtn = e.target.closest('[data-apply-filters]');
      if (applyBtn) {
        e.preventDefault();
        this.applyFromDrawer();
        this.closeDrawer();
        return;
      }
    });

    document.addEventListener('change', (e) => {
      const sortSelect = e.target.closest('[data-sort-select]');
      if (sortSelect) {
        this.activeSort = sortSelect.value;
        this.syncSortSelects();
        this.applySortAndFilter();
      }
    });
  }

  openDrawer() {
    const { drawer, backdrop } = this.getElements();
    if (drawer) drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
  }

  closeDrawer() {
    const { drawer, backdrop } = this.getElements();
    if (drawer) drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
  }

  syncSortSelects() {
    const { sortSelects } = this.getElements();
    sortSelects.forEach(select => {
      select.value = this.activeSort;
    });
  }

  applyFromDrawer() {
    const { availabilityCheckboxes, minPriceInputs, maxPriceInputs, sortSelects } = this.getElements();
    
    this.filters.inStock = availabilityCheckboxes.some(cb => cb.checked);

    const minVal = minPriceInputs.map(i => parseFloat(i.value)).find(v => !isNaN(v));
    this.filters.minPrice = minVal !== undefined ? minVal : null;

    const maxVal = maxPriceInputs.map(i => parseFloat(i.value)).find(v => !isNaN(v));
    this.filters.maxPrice = maxVal !== undefined ? maxVal : null;

    const drawerSort = sortSelects.find(s => s.hasAttribute('data-drawer-sort-select'));
    if (drawerSort && drawerSort.value) {
      this.activeSort = drawerSort.value;
      this.syncSortSelects();
    }

    this.applySortAndFilter();
  }

  resetFilters() {
    const { availabilityCheckboxes, minPriceInputs, maxPriceInputs, sortSelects } = this.getElements();
    
    availabilityCheckboxes.forEach(cb => cb.checked = false);
    minPriceInputs.forEach(i => i.value = '');
    maxPriceInputs.forEach(i => i.value = '');

    this.filters.inStock = false;
    this.filters.minPrice = null;
    this.filters.maxPrice = null;
    this.activeSort = 'featured';

    this.syncSortSelects();
    this.applySortAndFilter();
    this.closeDrawer();
  }

  updateBadgeCount() {
    const { badgeCounts } = this.getElements();
    let count = 0;
    if (this.filters.inStock) count++;
    if (this.filters.minPrice !== null) count++;
    if (this.filters.maxPrice !== null) count++;

    badgeCounts.forEach(badge => {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = 'inline-flex';
      } else {
        badge.style.display = 'none';
      }
    });
  }

  applySortAndFilter() {
    this.updateBadgeCount();

    const gridContainers = document.querySelectorAll('[data-grid-container]');
    gridContainers.forEach(grid => {
      const cards = Array.from(grid.querySelectorAll('.mobile-product-card'));
      if (cards.length === 0) return;

      // 1. Pre-map cards into objects for fast filtering & sorting
      const mappedCards = cards.map(card => ({
        el: card,
        available: card.getAttribute('data-available') === 'true',
        price: parseFloat(card.getAttribute('data-price')) / 100.0,
        priceRaw: parseFloat(card.getAttribute('data-price')) || 0,
        title: (card.getAttribute('data-title') || '').toLowerCase(),
        order: parseInt(card.getAttribute('data-original-order')) || 0
      }));

      // Filtering pass
      const visibleMapped = [];
      mappedCards.forEach(item => {
        let visible = true;
        if (this.filters.inStock && !item.available) visible = false;
        if (this.filters.minPrice !== null && item.price < this.filters.minPrice) visible = false;
        if (this.filters.maxPrice !== null && item.price > this.filters.maxPrice) visible = false;

        item.el.style.display = visible ? '' : 'none';
        if (visible) visibleMapped.push(item);
      });

      // 2. Sorting pass
      visibleMapped.sort((a, b) => {
        switch (this.activeSort) {
          case 'price-ascending':
            return a.priceRaw - b.priceRaw;
          case 'price-descending':
            return b.priceRaw - a.priceRaw;
          case 'title-ascending':
            return a.title.localeCompare(b.title);
          case 'title-descending':
            return b.title.localeCompare(a.title);
          case 'created-descending':
          case 'best-selling':
          case 'featured':
          default:
            return a.order - b.order;
        }
      });

      // Append in sorted order using DocumentFragment
      const fragment = document.createDocumentFragment();
      visibleMapped.forEach(item => fragment.appendChild(item.el));
      grid.appendChild(fragment);
    });
  }
};
}

// Global initialization
document.addEventListener('DOMContentLoaded', () => {
  const pdpSections = document.querySelectorAll('.mobile-pdp');
  pdpSections.forEach(sec => {
    sec._productManager = new MobileProductManager(sec);
  });

  window.quickAddInstance = new QuickAddManager();
  window.wishlistInstance = new WishlistManager();
  window.ajaxCartFormInstance = new AjaxCartFormHandler();
  window.sortFilterInstance = new GridSortFilterManager();
});

document.addEventListener('shopify:section:load', (e) => {
  const pdpSections = e.target.querySelectorAll('.mobile-pdp');
  pdpSections.forEach(sec => {
    sec._productManager = new MobileProductManager(sec);
  });
  if (window.wishlistInstance) {
    window.wishlistInstance.syncStates();
  }
  if (window.sortFilterInstance) {
    window.sortFilterInstance.refreshElements();
  } else {
    window.sortFilterInstance = new GridSortFilterManager();
  }
});
